import 'react-circular-progressbar/dist/styles.css';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import styled from 'styled-components';
import { Pool } from 'templates/pool/types';
import { getColorDistance, hexToRgb, similarityThreshold } from 'utils/colors';
import { media } from 'utils/media';
import { fontAdjusts } from 'utils/stringFormat';
import { isPoolVerified } from 'utils/verification';

export type MountedData = { target: string; raised: string; supporters: number };

const PoolProgressiveCard: FC<{
  data: Pool;
  fontColor?: string;
  isPrivate?: boolean;
  htmlFor?: string;
  isOnDescription: boolean;
  isOnBottom?: boolean;
  amount: number;
  onDonate?(): void;
  onMounted(data: MountedData): void;
}> = ({ data, fontColor: fc, isPrivate, htmlFor = 'donateInput', isOnDescription, amount, onDonate, onMounted }) => {
  const [showFixedPoolState, setHowFixedPoolState] = useState(false);

  const convertValue = useCallback((value?: number) => {
    if (!value) return 0;
    else return value / 100;
  }, []);

  const fixedFee = useMemo(() => (Number(data.raised) * Number(data.percentageFee)) / 100, [data]);

  const count = useMemo(() => {
    if ((!data.target || !data.raised) && data.raised !== 0 && data.target !== 0) return 0;

    return data.target && data.raised
      ? (((convertValue(data?.raised) - convertValue(fixedFee)) / parseFloat(data.target.toString())) * 100).toFixed(2)
      : 0;
  }, [data.target, data.raised, convertValue, fixedFee]);

  const formatAmount = (value: string | number) => {
    return value?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
  };

  const fontColor = useMemo(() => {
    const targetColor = { r: 255, g: 255, b: 255 };
    const distance = getColorDistance(targetColor, hexToRgb(fc || ''));

    if (distance < similarityThreshold) {
      return '#174F68';
    } else {
      return fc;
    }
  }, [fc]);

  const isVerified = useMemo(() => isPoolVerified(data), [data]);

  useEffect(() => {
    onMounted({
      target: formatAmount(data?.target || 0),
      raised: formatAmount(convertValue(data?.raised) - Number(convertValue(fixedFee))),
      supporters: data?.supporters ?? 0,
    });
  }, [convertValue, data?.raised, data?.supporters, data?.target, fixedFee, onMounted]);

  useEffect(() => {
    setHowFixedPoolState(false);
  }, []);

  return (
    <Container isOnDescription={isOnDescription}>
      <BadgeContainer>
        {isVerified && (
          <img
            src="/verificada.png"
            alt="Verificada"
            width="40"
            height="40"
            style={{
              width: 100,
              height: 70,
            }}
          />
        )}
      </BadgeContainer>
      <div className="progress-chart">
        {data.active !== false && (
          <CircularProgressbar
            styles={{
              ...buildStyles({
                // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                strokeLinecap: 'butt',
                // How long animation takes to go from one percentage to another, in seconds
                pathTransitionDuration: 0.5,

                // Colors
                pathColor: fontColor ? fontColor : `#174F68`,
                textColor: fontColor ? fontColor : '#174F68',
                trailColor: '#0E26491A',
                backgroundColor: fontColor ? fontColor : '#174F68',
                textSize: fontAdjusts(`${data.raised}`),
              }),
            }}
            maxValue={100}
            minValue={0}
            value={parseFloat(count.toString())}
            text={`${count}%`}
          />
        )}
        {data.active === false && (
          <div className="completed-badge-center">
            <img
              src="/concluida.png"
              alt="Concluída"
              width="40"
              height="40"
              style={{
                width: 185,
                height: 230,
              }}
            />
          </div>
        )}
      </div>
      <div className="progress-stats">
        <div>
          <span>Meta: </span>
          <strong
            style={{
              fontSize: fontAdjusts(`${data?.target}`, 18),
            }}
          >
            {formatAmount(data?.target || 0)}
          </strong>
        </div>
        <div>
          <span>Total arrecadado: </span>
          <strong
            style={{
              fontSize: fontAdjusts(`${data?.raised}`, 18),
            }}
          >
            {formatAmount(convertValue(data?.raised) - Number(convertValue(fixedFee)))}
          </strong>
        </div>
        <div>
          <span>Apoiadores</span>
          <strong
            style={{
              fontSize: fontAdjusts(`${data?.supporters}`, 18),
            }}
          >
            {data?.supporters || 0}
          </strong>
        </div>
      </div>
      {!isPrivate && (
        <>
          <Donate htmlFor={htmlFor} bg={fontColor ? fontColor : '#174F68'}>
            <span>{data.active === false ? 'Vaquinha Concluída' : 'Doar agora'}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_403_3007)">
                <path
                  d="M23.5351 14.7255C23.328 14.4739 23.0623 14.2771 22.7614 14.1523C22.4605 14.0274 22.1335 13.9784 21.8091 14.0094C20.0668 14.1472 18.3756 14.6641 16.854 15.524C15.3323 16.3839 14.017 17.5659 13.0001 18.9874V13.9104C14.3967 13.6732 15.6644 12.9499 16.5793 11.8685C17.4943 10.787 17.9975 9.417 18.0001 8.00045C18.0001 5.20745 16.0571 2.84845 14.1561 0.90945C13.5798 0.346296 12.806 0.0310059 12.0001 0.0310059C11.1943 0.0310059 10.4205 0.346296 9.84414 0.90945C7.94314 2.84845 6.00014 5.20745 6.00014 8.00045C6.00277 9.417 6.50601 10.787 7.42094 11.8685C8.33587 12.9499 9.6036 13.6732 11.0001 13.9104V18.9874C9.98325 17.5659 8.66795 16.3839 7.14632 15.524C5.62468 14.6641 3.93349 14.1472 2.19114 14.0094C1.86683 13.9786 1.53989 14.0277 1.23897 14.1525C0.938059 14.2773 0.672352 14.4741 0.465138 14.7255C0.259251 14.9699 0.115898 15.2608 0.0474331 15.5729C-0.0210313 15.8851 -0.0125527 16.2093 0.0721381 16.5175C2.11114 23.8554 11.5911 24.0005 11.9941 24.0005H12.0061C12.4061 24.0005 21.8901 23.8554 23.9291 16.5175C24.0133 16.2092 24.0215 15.885 23.9529 15.5729C23.8842 15.2608 23.7409 14.97 23.5351 14.7255ZM8.00014 8.00045C8.00014 5.34645 11.3181 2.28145 11.3381 2.26445C10.9731 3.20045 8.69314 8.90045 11.0001 9.00045C11.2654 9.00045 11.5197 8.89509 11.7072 8.70756C11.8948 8.52002 12.0001 8.26567 12.0001 8.00045C12.0756 6.22189 12.5029 4.47592 13.2571 2.86345C14.6881 4.39345 16.0001 6.15445 16.0001 8.00045C16.0001 9.06132 15.5787 10.0787 14.8286 10.8289C14.0784 11.579 13.061 12.0005 12.0001 12.0005C10.9393 12.0005 9.92186 11.579 9.17171 10.8289C8.42157 10.0787 8.00014 9.06132 8.00014 8.00045ZM2.00614 16.0005C3.80787 16.1464 5.53915 16.7654 7.02499 17.7949C8.51083 18.8243 9.69856 20.2278 10.4681 21.8635C7.97114 21.5175 3.20014 20.3004 2.00614 16.0005ZM13.5451 21.8615C14.321 20.2304 15.5091 18.83 16.9919 17.7988C18.4747 16.7676 20.201 16.1411 22.0001 15.9815C20.8061 20.2835 16.0461 21.5114 13.5451 21.8615Z"
                  fill={'#ffffff'}
                />
              </g>
              <defs>
                <clipPath id="clip0_403_3007">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </Donate>
          <MobileDonateContainer
            style={{
              transition: 'all .3s',
              bottom: showFixedPoolState ? '-0.5rem' : '-15rem',
              boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.15)'
            }}
            bg={fontColor ? fontColor : '#174F68'}
          >
            <div>
              <Donate htmlFor={htmlFor} bg={fontColor ? fontColor : '#174F68'}>
                <span>{data.active === false ? 'Vaquinha Concluída' : 'Doar agora'}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_403_3007)">
                    <path
                      d="M23.5351 14.7255C23.328 14.4739 23.0623 14.2771 22.7614 14.1523C22.4605 14.0274 22.1335 13.9784 21.8091 14.0094C20.0668 14.1472 18.3756 14.6641 16.854 15.524C15.3323 16.3839 14.017 17.5659 13.0001 18.9874V13.9104C14.3967 13.6732 15.6644 12.9499 16.5793 11.8685C17.4943 10.787 17.9975 9.417 18.0001 8.00045C18.0001 5.20745 16.0571 2.84845 14.1561 0.90945C13.5798 0.346296 12.806 0.0310059 12.0001 0.0310059C11.1943 0.0310059 10.4205 0.346296 9.84414 0.90945C7.94314 2.84845 6.00014 5.20745 6.00014 8.00045C6.00277 9.417 6.50601 10.787 7.42094 11.8685C8.33587 12.9499 9.6036 13.6732 11.0001 13.9104V18.9874C9.98325 17.5659 8.66795 16.3839 7.14632 15.524C5.62468 14.6641 3.93349 14.1472 2.19114 14.0094C1.86683 13.9786 1.53989 14.0277 1.23897 14.1525C0.938059 14.2773 0.672352 14.4741 0.465138 14.7255C0.259251 14.9699 0.115898 15.2608 0.0474331 15.5729C-0.0210313 15.8851 -0.0125527 16.2093 0.0721381 16.5175C2.11114 23.8554 11.5911 24.0005 11.9941 24.0005H12.0061C12.4061 24.0005 21.8901 23.8554 23.9291 16.5175C24.0133 16.2092 24.0215 15.885 23.9529 15.5729C23.8842 15.2608 23.7409 14.97 23.5351 14.7255ZM8.00014 8.00045C8.00014 5.34645 11.3181 2.28145 11.3381 2.26445C10.9731 3.20045 8.69314 8.90045 11.0001 9.00045C11.2654 9.00045 11.5197 8.89509 11.7072 8.70756C11.8948 8.52002 12.0001 8.26567 12.0001 8.00045C12.0756 6.22189 12.5029 4.47592 13.2571 2.86345C14.6881 4.39345 16.0001 6.15445 16.0001 8.00045C16.0001 9.06132 15.5787 10.0787 14.8286 10.8289C14.0784 11.579 13.061 12.0005 12.0001 12.0005C10.9393 12.0005 9.92186 11.579 9.17171 10.8289C8.42157 10.0787 8.00014 9.06132 8.00014 8.00045ZM2.00614 16.0005C3.80787 16.1464 5.53915 16.7654 7.02499 17.7949C8.51083 18.8243 9.69856 20.2278 10.4681 21.8635C7.97114 21.5175 3.20014 20.3004 2.00614 16.0005ZM13.5451 21.8615C14.321 20.2304 15.5091 18.83 16.9919 17.7988C18.4747 16.7676 20.201 16.1411 22.0001 15.9815C20.8061 20.2835 16.0461 21.5114 13.5451 21.8615Z"
                      fill={'#ffffff'}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_403_3007">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Donate>
            </div>
          </MobileDonateContainer>
        </>
      )}
    </Container>
  );
};

export default PoolProgressiveCard;

const Container = styled.div<{ isOnDescription: boolean }>`
  background-color: white;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
  padding: 2rem;
  width: 570px;
  min-height: 438px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;

  ${({ isOnDescription }) => (isOnDescription ? 'position: fixed !important;top: 3rem; z-index: 10;' : '')}

  .progress-chart {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    position: relative;

    & > *:first-child {
      font-family: 'Nunito';
      width: 200px;
      height: 200px;
      font-weight: bold;
      font-family: 24px;
    }

    .completed-badge-center {
      position: absolute;
      top: 40%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10;
    }
  }

  .progress-stats {
    display: flex;
    flex-direction: row;
    border-radius: 5px;
    background-color: #0e26491a;
    color: black;
    margin-top: 1rem;

    & > div {
      display: flex;
      flex-direction: column;
      text-align: center;
      flex: 1;
      padding: 1rem;

      & span {
        font-family: 'Nunito';
        font-size: 14px;
        font-weight: normal;
        text-align: center;
      }
      & strong {
        font-family: 'Nunito';
        font-size: 18px;
        font-weight: 700;
        line-height: 33px;
        letter-spacing: 0em;
        text-align: center;
      }

      &:nth-child(2) {
        border-left: 1px solid #0e264933;
        border-right: 1px solid #0e264933;
      }
    }
  }

  ${media('<=tablet')} {
    width: 100%;
    max-width: 380px;
    min-height: 329px;
    position: relative !important;
    z-index: auto;
    top: auto;

    .progress-chart {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      
      & > *:first-child {
        width: 150px;
        height: 150px;
      }

      .completed-badge-center {
        position: absolute;
        top: -10%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        img {
          width: 150px !important;
          height: 180px !important;
          margin-bottom: 20px;
        }
      }
    }

    .progress-stats {
      & span {
        font-size: 12px !important;
      }
      & strong {
        font-size: 14px !important;
      }
    }
  }

  ${media('<=phone')} {
    width: 300px;
    min-height: 329px;

    .progress-chart {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      
      & > *:first-child {
        width: 100px;
        height: 100px;
      }

      .completed-badge-center {
        position: absolute;
        top: 15%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        
        @media (max-width: 500px) {
          img {
            width: 100px !important;
            height: 130px !important;
            margin-bottom: 0px;
          }
        }
      }
    }

    & * {
      font-size: 100% !important;
    }
  }
`;

const Donate = styled.label<{ bg?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ bg }) => bg};
  padding: 1.5rem 0;
  border-radius: 10px;
  margin-top: 1.5rem;
  font-weight: bold;
  font-size: 12pt;
  cursor: pointer !important;
  color: white !important;

  & > *:last-child {
    margin-left: 1rem;
  }
`;

const MobileDonateContainer = styled.div<{ bg?: string }>`
  padding: 0 4rem;
  display: none;
  background-color: white;
  color: black;
  padding-bottom: 2rem;

  ${media('<=tablet')} {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 10;
    flex-direction: column;

    & > div {
      display: flex;
      justify-content: center;
      align-items: center;

      :last-child label {
        align-self: center;
        padding: 1rem 2rem;
        color: white;
        width: 100% !important;
      }
    }
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-bottom: 1rem;
  gap: 10px;
  min-height: 70px;
`;
